import React from 'react';
import { Card, Button, IconButton, EmptyState, Badge } from '@parsina/framework';
import { Plus, Pencil, Trash2 } from 'lucide-react';

import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { HISTORY_SECTIONS, VITAL_FIELDS } from '../../lib/history.js';
import { groupBySection, sourceKey, vitalPairs, labPairs } from './historyForm.js';

// One section of the شرح حال, as it is READ.
//
// Shared by both surfaces: what a section's entries look like is the record, and
// the record does not change shape between a laptop and a phone — only the chrome
// around it does. The overlay a student edits in is the surface's own (a Dialog
// on the desktop, a BottomSheet on the phone), which is why this component
// reports intent through `onAdd`/`onEdit`/`onDelete` rather than owning one.

const sectionMeta = (id) => HISTORY_SECTIONS.find((s) => s.id === id) || HISTORY_SECTIONS[0];
const vitalLabelKey = (key) => VITAL_FIELDS.find((f) => f.key === key)?.labelKey || null;
const vitalUnitKey = (key) => VITAL_FIELDS.find((f) => f.key === key)?.unitKey || null;

function VitalsBlock({ structured }) {
  const { t, formatDigits } = useTranslation();
  const pairs = vitalPairs(structured);
  if (!pairs.length) return null;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
        gap: 8,
        marginTop: 10,
      }}
    >
      {pairs.map(({ key, value }) => {
        const labelKey = vitalLabelKey(key);
        const unitKey = vitalUnitKey(key);
        return (
          <div key={key} style={{ background: CT.bg, borderRadius: 10, padding: '8px 10px' }}>
            <div style={{ fontSize: 10.5, color: C.textMuted }}>
              {/* An unknown key is printed RAW rather than through `t()`: the
                  payload is stored opaque and a future writer could put anything
                  in it, and a raw «xyz» is honest where a dotted catalogue key
                  masquerading as a label is not. */}
              {labelKey ? t(labelKey) : key}
            </div>
            <div
              style={{ fontSize: 14, fontWeight: 800, color: C.textPrimary, marginTop: 2 }}
              // A reading plus its unit is a digit run: it reads left-to-right
              // inside either layout, and `isolate` is what stops the adjacent
              // punctuation being dragged to the wrong end.
              dir="ltr"
            >
              <span style={{ unicodeBidi: 'isolate' }}>{formatDigits(value)}</span>
              {unitKey && (
                <span style={{ fontSize: 10.5, fontWeight: 600, color: C.textMuted, marginInlineStart: 4 }}>
                  {t(unitKey)}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function LabsBlock({ structured }) {
  const { t, formatDigits } = useTranslation();
  const rows = labPairs(structured);
  if (!rows.length) return null;

  return (
    <div style={{ marginTop: 10, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
        <thead>
          <tr>
            {['name', 'value', 'unit', 'ref'].map((key) => (
              <th
                key={key}
                style={{
                  textAlign: 'start',
                  padding: '6px 8px',
                  color: C.textSecondary,
                  fontWeight: 700,
                  fontSize: 11,
                  borderBottom: `1px solid ${C.border}`,
                  whiteSpace: 'nowrap',
                }}
              >
                {t(`history.labs.${key}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={`${row?.name || 'row'}-${index}`}>
              <td style={{ padding: '6px 8px', borderBottom: `1px solid ${C.border}`, fontWeight: 700 }}>
                {row?.name || t('common.labels.none')}
              </td>
              {['value', 'unit', 'ref'].map((key) => (
                <td
                  key={key}
                  style={{ padding: '6px 8px', borderBottom: `1px solid ${C.border}`, color: CT.ink2 }}
                  dir="ltr"
                >
                  <span style={{ unicodeBidi: 'isolate' }}>
                    {row?.[key] ? formatDigits(String(row[key])) : t('common.labels.none')}
                  </span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function HistoryEntryCard({ entry, onEdit, onDelete, visitSeq = null }) {
  const { t, formatDate, formatNumber } = useTranslation();

  return (
    <Card style={{ padding: 14 }} data-testid={`entry-${entry.id}`}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {entry.label && (
            <div style={{ fontSize: 13, fontWeight: 800, color: C.textPrimary, marginBottom: 4 }}>
              {entry.label}
            </div>
          )}
          {/* `pre-wrap`: a student's history has paragraphs and line breaks in it,
              and collapsing them turns a structured history into a wall. */}
          <div
            style={{
              fontSize: 13.5,
              lineHeight: 2,
              color: C.textPrimary,
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
            }}
          >
            {entry.body}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          <IconButton
            variant="ghost"
            size={36}
            icon={<Pencil size={15} />}
            label={t('common.actions.edit')}
            onClick={() => onEdit?.(entry)}
          />
          <IconButton
            variant="ghost"
            size={36}
            icon={<Trash2 size={15} color={C.red} />}
            label={t('common.actions.delete')}
            onClick={() => onDelete?.(entry)}
          />
        </div>
      </div>

      {entry.section === 'vitals' && <VitalsBlock structured={entry.structured} />}
      {entry.section === 'labs' && <LabsBlock structured={entry.structured} />}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          marginTop: 10,
          paddingTop: 10,
          borderTop: `1px solid ${C.border}`,
          fontSize: 11,
          color: C.textMuted,
        }}
      >
        {/*
          PROVENANCE, on every entry, always.

          «شما» / «شما، از پیش‌نویس صوتی» / «شما، از پیشنهاد هوش مصنوعی». All three
          begin with «شما» because all three ARE the student's: the AI never writes
          an entry — `parseCaseVoice` returns a draft that a human confirms. This
          badge is what lets a student go back and re-check which sentences a model
          put in their mouth, and it is exactly why `source` is not updatable.
        */}
        <Badge variant={entry.source === 'manual' ? 'outline' : 'warning'}>
          {t('history.entry.sourceLabel')}: {t(sourceKey(entry.source))}
        </Badge>
        {/*
          «ویزیت ۳» — this fact was written INSIDE a visit.

          The chip is small and quiet because the entry belongs to the patient,
          not to the visit: cancelling visit 3 keeps this row and only clears
          the stamp (core.md §5.1). It renders only when the caller could
          resolve the visit's number — an entry whose encounter is gone shows
          nothing rather than «ویزیت null».
        */}
        {visitSeq != null && (
          <Badge variant="outline" data-testid={`entry-visit-${entry.id}`}>
            {t('record.history.visitChip', { n: formatNumber(visitSeq) })}
          </Badge>
        )}
        {entry.recordedAtMs && (
          <span>
            {t('history.entry.recordedAt')}: {formatDate(entry.recordedAtMs, 'numeric')}
          </span>
        )}
        <span style={{ marginInlineStart: 'auto' }}>{formatDate(entry.createdAtMs, 'numeric')}</span>
      </div>
    </Card>
  );
}

export default function HistorySection({ section, entries, onAdd, onEdit, onDelete }) {
  const { t } = useTranslation();
  const meta = sectionMeta(section);
  const rows = groupBySection(entries)[section] || [];
  const { Icon } = meta;

  return (
    <section
      role="tabpanel"
      aria-label={t(meta.labelKey)}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon size={17} color={C.teal} aria-hidden />
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: C.textPrimary }}>
          {t(meta.labelKey)}
        </h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onAdd?.(section)}
          style={{ marginInlineStart: 'auto' }}
          data-testid="history-add"
        >
          <Plus size={14} aria-hidden />
          {t('history.add')}
        </Button>
      </div>

      {rows.length ? (
        rows.map((entry) => (
          <HistoryEntryCard key={entry.id} entry={entry} onEdit={onEdit} onDelete={onDelete} />
        ))
      ) : (
        // An empty section is EMPTY, not broken — the student has simply not
        // written it yet, and the copy says what writing it is for: this is what
        // the assistant reads.
        <Card>
          <EmptyState
            icon={<Icon size={40} color={C.textMuted} />}
            title={t('history.empty.title')}
            description={t('history.empty.body')}
            action={<Button onClick={() => onAdd?.(section)}>{t('history.add')}</Button>}
          />
        </Card>
      )}
    </section>
  );
}
